/**
 * Pure helpers for the Recurring pages (tracker item 29): labels, the create
 * form's validation and request body, and the generate-now summary. Dates
 * stay YYYY-MM-DD strings throughout; nothing goes through local-time Date.
 */

import * as types from '../api/types';
import {
  addDays,
  dayOfMonth,
  dayOfWeek,
  frequencyConfigError,
  isDateOnly,
  lastDayOfMonth,
  listOccurrences,
} from '../utils/recurrence';
import { formatJobDate, formatJobMoney } from '../utils/jobPresentation';

export const RECURRING_PATH = '/recurring';
export const RECURRING_NEW_PATH = '/recurring/new';
export const recurringDetailPath = (id: string) => `/recurring/${id}`;

/** /recurring/new prefilled from a customer, property or DRAFT template job. */
export function recurringNewPath(prefill: { customerId?: string; propertyId?: string; templateJobId?: string } = {}): string {
  const params = new URLSearchParams();
  (['customerId', 'propertyId', 'templateJobId'] as const).forEach((key) => {
    const value = prefill[key];
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return query ? `${RECURRING_NEW_PATH}?${query}` : RECURRING_NEW_PATH;
}

export const FREQUENCY_LABELS: Record<types.RecurringFrequency, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually',
};

/** The unit "Repeat every N ..." counts in. */
export const INTERVAL_UNITS: Record<types.RecurringFrequency, [string, string]> = {
  DAILY: ['day', 'days'],
  WEEKLY: ['week', 'weeks'],
  MONTHLY: ['month', 'months'],
  QUARTERLY: ['quarter', 'quarters'],
  ANNUALLY: ['year', 'years'],
};

const STATUS_LABELS: Record<string, string> = { ACTIVE: 'Active', PAUSED: 'Paused', CANCELLED: 'Cancelled' };

export function recurringStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function recurringStatusClass(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'bg-green-50 text-green-800 ring-green-200';
    case 'PAUSED': return 'bg-amber-50 text-amber-800 ring-amber-200';
    default: return 'bg-gray-100 text-gray-700 ring-gray-200';
  }
}

const BILLING_LABELS: Record<string, string> = { PER_VISIT: 'Per visit', FLAT_CONTRACT: 'Flat contract' };

/** Label for a stored billing model; schedules without one are per visit. */
export function billingModelLabel(model: string | null | undefined): string {
  return BILLING_LABELS[model || 'PER_VISIT'] ?? model ?? 'Per visit';
}

export const BILLING_FREQUENCY_LABELS: Record<types.FlatContractBillingFrequency, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually',
};

const BILLING_UNITS: Record<types.FlatContractBillingFrequency, [string, string]> = {
  WEEKLY: ['week', 'weeks'],
  MONTHLY: ['month', 'months'],
  QUARTERLY: ['quarter', 'quarters'],
  ANNUALLY: ['year', 'years'],
};

/** "week" / "weeks" for the billing interval input. */
export function billingIntervalUnit(frequency: types.FlatContractBillingFrequency, interval: number): string {
  const [one, many] = BILLING_UNITS[frequency];
  return interval === 1 ? one : many;
}

/** "Per visit", or "Flat contract: $250.00 per month" / "... every 2 months". */
export function billingTermsLabel(
  schedule: Pick<types.RecurringSchedule, 'billingModel' | 'flatContractAmount' | 'billingFrequency' | 'billingInterval'>
): string {
  if (schedule.billingModel !== 'FLAT_CONTRACT') return billingModelLabel(schedule.billingModel);
  if (!schedule.flatContractAmount || !schedule.billingFrequency) return 'Flat contract';
  const interval = schedule.billingInterval || 1;
  const period = interval === 1
    ? `per ${BILLING_UNITS[schedule.billingFrequency][0]}`
    : `every ${interval} ${BILLING_UNITS[schedule.billingFrequency][1]}`;
  return `Flat contract: ${formatJobMoney(schedule.flatContractAmount)} ${period}`;
}

// ============ Billing form (create and edit) ============

export interface BillingForm {
  model: types.RecurringBillingModel;
  /** Text from the amount input */
  amount: string;
  frequency: types.FlatContractBillingFrequency;
  /** Text from the number input */
  interval: string;
}

export function defaultBillingForm(): BillingForm {
  return { model: 'PER_VISIT', amount: '', frequency: 'MONTHLY', interval: '1' };
}

/** The form for a stored schedule's terms. */
export function billingFormFrom(
  schedule: Pick<types.RecurringSchedule, 'billingModel' | 'flatContractAmount' | 'billingFrequency' | 'billingInterval'>
): BillingForm {
  return {
    model: schedule.billingModel === 'FLAT_CONTRACT' ? 'FLAT_CONTRACT' : 'PER_VISIT',
    amount: schedule.flatContractAmount ?? '',
    frequency: schedule.billingFrequency ?? 'MONTHLY',
    interval: String(schedule.billingInterval || 1),
  };
}

const AMOUNT = /^\d+(\.\d{1,2})?$/;

/** Field errors (flat contract only): amount > 0 with at most 2 decimals, interval 1-120. */
export function billingFormErrors(form: BillingForm): Partial<Record<'amount' | 'interval', string>> {
  if (form.model !== 'FLAT_CONTRACT') return {};
  const errors: Partial<Record<'amount' | 'interval', string>> = {};
  const amount = form.amount.trim();
  if (!amount) errors.amount = 'Enter the contract amount.';
  else if (!AMOUNT.test(amount) || !(Number(amount) > 0)) errors.amount = 'Enter an amount greater than 0 with at most 2 decimals.';
  const interval = parseInterval(form.interval);
  if (!Number.isInteger(interval) || interval < 1 || interval > 120) errors.interval = 'Enter a whole number from 1 to 120.';
  return errors;
}

/** The billing fields of a create / PATCH body (PER_VISIT sends only the model). */
export function buildBillingTerms(form: BillingForm): types.RecurringBillingTerms {
  if (form.model !== 'FLAT_CONTRACT') return { billingModel: 'PER_VISIT' };
  return {
    billingModel: 'FLAT_CONTRACT',
    flatContractAmount: form.amount.trim(),
    billingFrequency: form.frequency,
    billingInterval: parseInterval(form.interval),
  };
}

/** "123 Main St, Springfield, IL" */
export function scheduleAddress(property: { address: string; city?: string; state?: string }): string {
  return [property.address, property.city, property.state].filter(Boolean).join(', ');
}

/** The template job is usable for generation only while it is still a DRAFT. */
export function templateProblem(schedule: Pick<types.RecurringSchedule, 'templateJobId' | 'templateJob'>): string | null {
  if (!schedule.templateJobId || !schedule.templateJob?.id) {
    return 'The template job no longer exists, so no jobs can be generated. Create a new schedule from a draft job.';
  }
  if (schedule.templateJob.status !== types.JobStatus.DRAFT) {
    return `The template job is ${schedule.templateJob.status.toLowerCase().replace('_', ' ')}, not a draft, so generation will fail. Templates must stay in DRAFT.`;
  }
  return null;
}

/** Why a DRAFT job cannot be a template (backend validateTemplateJob), or null. */
export function templateJobIssue(job: Pick<types.Job, 'status' | 'assignedTo' | 'lineItems'>): string | null {
  if (job.status !== types.JobStatus.DRAFT) return 'not a draft';
  if (!job.assignedTo) return 'needs an assignee';
  if (!job.lineItems || job.lineItems.length === 0) return 'needs a work item';
  return null;
}

// ============ Create form ============

export type MonthlyMode = 'DAY_OF_MONTH' | 'NTH_WEEKDAY';

export interface RecurrenceForm {
  frequency: types.RecurringFrequency;
  /** Text from the number input */
  interval: string;
  daysOfWeek: number[];
  monthlyMode: MonthlyMode;
  dayOfMonth: string;
  ordinal: string;
  weekday: string;
  startDate: string;
  endDate: string;
}

export function defaultRecurrenceForm(startDate: string): RecurrenceForm {
  const valid = isDateOnly(startDate);
  return {
    frequency: 'WEEKLY',
    interval: '1',
    daysOfWeek: valid ? [dayOfWeek(startDate)] : [],
    monthlyMode: 'DAY_OF_MONTH',
    dayOfMonth: valid ? String(dayOfMonth(startDate)) : '1',
    ordinal: '1',
    weekday: valid ? String(dayOfWeek(startDate)) : '1',
    startDate,
    endDate: '',
  };
}

/** The frequencyDetails the form describes (always explicit for WEEKLY / MONTHLY). */
export function buildFrequencyDetails(form: RecurrenceForm): types.RecurringFrequencyDetails | undefined {
  switch (form.frequency) {
    case 'WEEKLY':
      return { daysOfWeek: [...form.daysOfWeek].sort((a, b) => a - b) };
    case 'MONTHLY':
      return form.monthlyMode === 'NTH_WEEKDAY'
        ? { ordinal: Number(form.ordinal), dayOfWeek: Number(form.weekday) }
        : { dayOfMonth: Number(form.dayOfMonth) };
    default:
      return undefined;
  }
}

export function parseInterval(value: string): number {
  return /^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN;
}

/**
 * The first date on or after `form.startDate` that is on the pattern. The
 * backend lists the start date itself as the first occurrence (except for
 * "nth weekday", which snaps forward), so an off-pattern start would get a
 * job on the wrong day. Null when the form is not valid enough to tell.
 */
export function firstPatternDate(form: RecurrenceForm): string | null {
  if (!isDateOnly(form.startDate)) return null;
  const start = form.startDate;
  switch (form.frequency) {
    case 'WEEKLY': {
      if (form.daysOfWeek.length === 0) return null;
      for (let offset = 0; offset < 7; offset += 1) {
        const date = addDays(start, offset);
        if (form.daysOfWeek.includes(dayOfWeek(date))) return date;
      }
      return null;
    }
    case 'MONTHLY': {
      if (form.monthlyMode === 'NTH_WEEKDAY') return start;
      const wanted = Number(form.dayOfMonth);
      if (!Number.isInteger(wanted) || wanted < 1 || wanted > 31) return null;
      const thisMonth = `${start.slice(0, 8)}${String(Math.min(wanted, lastDayOfMonth(start))).padStart(2, '0')}`;
      if (thisMonth >= start) return thisMonth;
      const nextMonth = `${addDays(`${start.slice(0, 8)}01`, 32).slice(0, 8)}01`;
      return `${nextMonth.slice(0, 8)}${String(Math.min(wanted, lastDayOfMonth(nextMonth))).padStart(2, '0')}`;
    }
    default:
      return start;
  }
}

/** Every problem with the form, keyed by field ('form' for the pattern as a whole). */
export function recurrenceFormErrors(form: RecurrenceForm): Partial<Record<'interval' | 'pattern' | 'startDate' | 'endDate', string>> {
  const errors: Partial<Record<'interval' | 'pattern' | 'startDate' | 'endDate', string>> = {};
  const interval = parseInterval(form.interval);
  if (!Number.isInteger(interval) || interval < 1) errors.interval = 'Enter a whole number of 1 or more.';
  else if (interval > 99) errors.interval = 'Enter 99 or less.';

  const details = buildFrequencyDetails(form);
  if (form.frequency === 'MONTHLY' && form.monthlyMode === 'DAY_OF_MONTH' && !/^\d+$/.test(form.dayOfMonth.trim())) {
    errors.pattern = 'Day of the month must be 1 to 31.';
  } else {
    const problem = frequencyConfigError(form.frequency, Number.isInteger(interval) && interval >= 1 ? interval : 1, details);
    if (problem) errors.pattern = problem;
  }

  if (!isDateOnly(form.startDate)) errors.startDate = 'Enter a valid start date.';
  else if (!errors.pattern) {
    const first = firstPatternDate(form);
    if (first && first !== form.startDate) {
      errors.startDate = `The start date is not on the pattern (the first visit is the start date itself). The first matching date is ${formatJobDate(first)}.`;
    }
  }

  if (form.endDate) {
    if (!isDateOnly(form.endDate)) errors.endDate = 'Enter a valid end date, or leave it blank.';
    else if (isDateOnly(form.startDate) && form.endDate < form.startDate) errors.endDate = 'The end date must be on or after the start date.';
  }
  return errors;
}

/** The POST /recurring-jobs body, with the billing terms (per visit unless a flat contract is chosen). */
export function buildCreateScheduleRequest(
  form: RecurrenceForm,
  propertyId: string,
  templateJobId: string,
  notes: string,
  billing: BillingForm = defaultBillingForm()
): types.CreateRecurringScheduleRequest {
  const details = buildFrequencyDetails(form);
  const trimmedNotes = notes.trim();
  return {
    propertyId,
    templateJobId,
    frequency: form.frequency,
    interval: parseInterval(form.interval),
    ...(details ? { frequencyDetails: details } : {}),
    startDate: form.startDate,
    ...(form.endDate ? { endDate: form.endDate } : {}),
    ...(trimmedNotes ? { notes: trimmedNotes } : {}),
    ...buildBillingTerms(billing),
  };
}

/** The first few dates the form's pattern produces (a client-side sketch; the schedule page shows the server preview). */
export function sampleDates(form: RecurrenceForm, count = 5): string[] {
  if (Object.keys(recurrenceFormErrors(form)).length > 0) return [];
  return listOccurrences(form.startDate, form.frequency, parseInterval(form.interval), buildFrequencyDetails(form), form.endDate || null, count);
}

/** The UTC calendar date the server uses for "due" (generation creates dates on or before it). */
export function utcToday(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

// ============ Generate now ============

export function generationSummary(result: types.RecurringGenerationResult): string {
  const created = result.generatedCount;
  const failed = result.failedCount;
  if (created === 0 && failed === 0) {
    return 'Nothing new to generate: every due date already has a job (or is skipped). No jobs were created.';
  }
  const parts = [`Created ${created} ${created === 1 ? 'job' : 'jobs'}.`];
  if (failed > 0) parts.push(`${failed} ${failed === 1 ? 'date' : 'dates'} failed and will be retried on the next run.`);
  return parts.join(' ');
}
